import React, { useState, useEffect, useRef } from 'react';
import { FiBell } from 'react-icons/fi';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notifText } from '../utils/notificationText';
import { fmtDate } from '../utils/fmtDate';
import toast from 'react-hot-toast';
import useVisiblePolling from '../hooks/useVisiblePolling';
import { BACKGROUND_READ_TIMEOUT_MS } from '../utils/readTimeout';
import { fetchAuthorizedBranches, applyActiveBranch, effectiveBranchId } from '../utils/authorizedBranches';

const NotificationDropdown = () => {
    const { t, i18n } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const fetchNotifications = async () => {
        try {
            // Bounded (B02): a hung read would otherwise hold the poller's
            // single in-flight slot and freeze the bell for the session. The
            // list already on screen survives the timeout.
            const res = await axios.get('/api/notifications', {
                timeout: BACKGROUND_READ_TIMEOUT_MS,
            });
            if (res.data.success) {
                setNotifications(res.data.data);
                setUnreadCount(res.data.data.filter(n => !n.is_read).length);
            }
        } catch (error) {
            // ponytail: shared with the 30s poll below — toasting here would spam
            // on every failed cycle, so this stays a console-only background miss.
            console.error('Failed to fetch notifications', error);
        }
    };

    // Poll every 30 seconds while the tab is visible; a hidden tab stops asking
    // and resyncs on return (P01). Opening the bell always shows the last
    // fetched list plus that resync, so nothing is silently dropped.
    useVisiblePolling(fetchNotifications, 30000);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const markAsRead = async (id, link) => {
        try {
            await axios.patch(`/api/notifications/${id}/read`);
            setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to mark as read', error);
            toast.error(t('somethingWentWrong'));
        }
    };

    // The bell lists the caller's rows from every branch they may open, but a
    // document page reads on the picked branch. A row from another branch
    // switches the picker first (the picker's own action), then opens the link
    // with a full load so every read runs under the new branch.
    const isOtherBranch = (n) =>
        n.garage_id != null && String(n.garage_id) !== String(effectiveBranchId() || '');

    const openOtherBranch = async (n) => {
        setIsOpen(false);
        let allowed = [];
        try {
            allowed = await fetchAuthorizedBranches();
        } catch (error) {
            console.error('Failed to load branches', error);
        }
        if (!allowed.some((b) => String(b.id) === String(n.garage_id))) {
            toast.error(t('err.FA_GARAGE_FORBIDDEN'));
            return;
        }
        applyActiveBranch(n.garage_id);
        window.location.assign(n.link);
    };

    const onViewDetails = (e, n) => {
        markAsRead(n.id);
        if (isOtherBranch(n)) {
            e.preventDefault();
            openOtherBranch(n);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.patch('/api/notifications/read-all');
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
            toast.success(t('cm.notifications.allMarkedRead'));
        } catch (error) {
            toast.error(t('cm.notifications.markAllFailed'));
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2.5 rounded-lg hover:bg-surface-2 text-content-secondary hover:text-content transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('cm.notifications.title')}
            >
                <FiBell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute top-1.5 end-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-surface"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute end-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-surface rounded-xl shadow-xl border border-edge overflow-hidden z-50">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-edge bg-surface-2/60">
                        <h3 className="font-semibold text-content text-sm">{t('cm.notifications.title')}</h3>
                        {unreadCount > 0 && (
                            <button onClick={markAllRead} className="text-xs text-accent hover:text-accent-hover font-medium">
                                {t('cm.notifications.markAllRead')}
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-6 text-center text-content-muted flex flex-col items-center">
                                <FiBell className="mb-2 opacity-30" size={32} />
                                <p className="text-sm">{t('cm.notifications.empty')}</p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`p-4 border-b border-edge last:border-b-0 hover:bg-surface-2/60 transition-colors ${!notification.is_read ? 'bg-accent-soft/40' : ''}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`mt-1 min-w-[8px] h-2 rounded-full ${!notification.is_read ? 'bg-accent' : 'bg-transparent'}`}></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-content">{notifText(t, notification.title, notification.params)}</p>
                                            <p className="text-sm text-content-secondary mt-0.5 line-clamp-2">{notifText(t, notification.message, notification.params)}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs text-content-muted">{fmtDate(notification.created_at, i18n.language)}</span>
                                                {notification.link && (
                                                    <Link
                                                        to={notification.link}
                                                        onClick={(e) => onViewDetails(e, notification)}
                                                        className="text-xs text-accent hover:text-accent-hover font-medium flex items-center"
                                                    >
                                                        {t('cm.notifications.viewDetails')}
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
